import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePieceDispatch, usePieceSelector } from '../../model/piece-context';
import type { AudioLayer } from '../../model/types';
import type { SoundPanelState } from './use-sound-panel-state';

const VOICEOVER_TRACK_LABEL_MAX_CHARS = 30;
const VOICEOVER_FALLBACK_LABEL = 'Locución';

/** Builds the voiceover label from the script. */
const buildVoiceoverLabel = (script: string): string => {
  const trimmed = script.trim();
  if (trimmed.length === 0) return VOICEOVER_FALLBACK_LABEL;
  if (trimmed.length > VOICEOVER_TRACK_LABEL_MAX_CHARS) {
    return `${trimmed.slice(0, VOICEOVER_TRACK_LABEL_MAX_CHARS)}…`;
  }
  return trimmed;
};

type UseApplyAudioClipResult = {
  /** Adds the active tab's audio to the editor model and resets that tab's state. */
  handleApply: () => void;

  /** Whether the current tab has enough data to add an audio layer. */
  canApply: boolean;

  /** Whether the audio duration is still being resolved. */
  isApplyProbing: boolean;
};

/**
 * Adds the selected voiceover or music as a new audio layer in the editor model.
 * Each applied clip becomes its own layer, allowing multiple audio clips to coexist.
 * After applying, only the active tab is reset.
 */
export const useApplyAudioClip = (state: SoundPanelState): UseApplyAudioClipResult => {
  const dispatch = usePieceDispatch();
  const pieceDuration = usePieceSelector(m => m.duration);

  const handleApply = useCallback(() => {
    if (state.activeTab === 'voiceover') {
      const { voiceover } = state;
      if (!voiceover.audioUrl || voiceover.audioDurationMs === null) return;
      const layer: AudioLayer = {
        id: uuidv4(),
        kind: 'audio',
        visible: true,
        locked: false,
        label: buildVoiceoverLabel(voiceover.script),
        start: 0,
        duration: voiceover.audioDurationMs,
        fit: 'fixed',
        content: { src: voiceover.audioUrl, gain: voiceover.volume, source: 'tts', script: voiceover.script }
      };
      dispatch({ type: 'addAudioLayer', layer });
      voiceover.setScript('');
      voiceover.setAudioUrl(null);
      voiceover.setAudioDurationMs(null);
      // Keep the volume as the default for the next clip.
      return;
    }

    if (state.activeTab === 'music') {
      const { music } = state;
      if (!music.selectedTrackId || music.selectedTrackDurationMs === null || !music.tracks) return;
      const track = music.tracks.find(t => t.id === music.selectedTrackId);
      if (!track) return;
      const layer: AudioLayer = {
        id: uuidv4(),
        kind: 'audio',
        visible: true,
        locked: false,
        label: track.name,
        start: 0,
        duration: Math.min(music.selectedTrackDurationMs, pieceDuration),
        fit: 'fixed',
        content: {
          src: track.url,
          gain: music.volume,
          source: 'library',
          trackId: track.id,
          trackName: track.name,
          origin: 'editor'
        }
      };
      dispatch({ type: 'addAudioLayer', layer });
      music.setSelectedTrackId(null);
      music.setSelectedTrackDurationMs(null);
      // Keep the volume as the default for the next clip.
    }
  }, [state, dispatch]);

  const canApply =
    state.activeTab === 'voiceover'
      ? !!state.voiceover.audioUrl && state.voiceover.audioDurationMs !== null && !state.voiceover.isGenerating
      : !!state.music.selectedTrackId && state.music.selectedTrackDurationMs !== null;

  const isApplyProbing =
    state.activeTab === 'voiceover'
      ? !!state.voiceover.audioUrl && state.voiceover.audioDurationMs === null && !state.voiceover.isGenerating
      : !!state.music.selectedTrackId && state.music.selectedTrackDurationMs === null && !!state.music.tracks;

  return { handleApply, canApply, isApplyProbing };
};
